import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";
import { useCustomersList } from "../../hooks/useCloudSelectors";
import config from "config";
import { CustomerConfig } from "../../../src/Dashboard/actions/apiActions";
import {
    changeCreateCustomerViewState,
    changeEditViewState,
    updateCustomersList
} from "../../ducks/customersDuck";
import DeleteModal from "../DeleteModal/DeleteModal";
import {
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";

const CustomerEditView = ({ customer, createCustomerVisible }) => {
    console.log("customer in edit view", customer);
    const isNew = createCustomerVisible;

    const [customerId, setCustomerId] = useState(customer.customerId);
    const [address, setAddress] = useState(customer.address);
    const [longitude, setLongitude] = useState(customer.gpsLong);
    const [customerName, setCustomerName] = useState(customer.customerName);
    const [city, setCity] = useState(customer.city);
    const [latitude, setLatitude] = useState(customer.gpsLat);

    const [dataChanged, setDataChanged] = useState(false);
    const [errors, setErrors] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);
    const allCustomers = useCustomersList();
    const dispatch = useDispatch();
    const initialRender = useRef(true);

    // Sync form when customer prop changes (switching between sites)
    useEffect(() => {
        setCustomerId(customer.customerId);
        setAddress(customer.address);
        setLongitude(customer.gpsLong);
        setCustomerName(customer.customerName);
        setCity(customer.city);
        setLatitude(customer.gpsLat);
        setDataChanged(false);
        setErrors({});
        initialRender.current = true;
    }, [customer.customerName]);

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
        } else {
            setDataChanged(true);
        }
    }, [customerId, address, longitude, customerName, city, latitude]);

    useEffect(() => {
        if (isNew) setDataChanged(true);
    }, []);

    const validate = () => {
        const e = {};
        if (!customerId) e.customerId = "Required";
        if (!customerName) e.customerName = "Required";
        if (!city) e.city = "Required";
        if (!address) e.address = "Required";
        if (!latitude && latitude !== 0) e.latitude = "Required";
        else if (isNaN(latitude)) e.latitude = "Must be a number";
        if (!longitude && longitude !== 0) e.longitude = "Required";
        else if (isNaN(longitude)) e.longitude = "Must be a number";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        if (isNew) {
            const updatedCustomersObject = {
                customers: [...allCustomers, { siteId: customerId, siteName: customerName, customerId, customerName, city, address, gpsLat: latitude, gpsLong: longitude, devices: [] }]
            };
            await CustomerConfig.post(config.CONFIGURATION_URL, updatedCustomersObject);
            setDataChanged(false);
            dispatch(updateCustomersList(updatedCustomersObject.customers));
            dispatch(changeEditViewState(customerName));
            console.log("updatedCustomersObject", updatedCustomersObject);
        } else {
            const updatedCustomersObject = {
                customers: allCustomers.map(c => c.customerName === customer.customerName
                    ? { ...c, siteId: customerId, siteName: customerName, customerId, customerName, city, address, gpsLat: latitude, gpsLong: longitude }
                    : c
                )
            };
            console.log("updatedCustomersObject", updatedCustomersObject);
            // await CustomerConfig.post(config.CONFIGURATION_URL, updatedCustomersObject);
            // setDataChanged(false);
            // dispatch(updateCustomersList(updatedCustomersObject.customers));
        }
    };

    const handleReset = () => {
        if (isNew) {
            dispatch(changeCreateCustomerViewState());
            return;
        }
        setCustomerId(customer.customerId);
        setAddress(customer.address);
        setLongitude(customer.gpsLong);
        setCustomerName(customer.customerName);
        setCity(customer.city);
        setLatitude(customer.gpsLat);
        setErrors({});
        setTimeout(() => setDataChanged(false), 0);
    };

    return (
        <>
            {deleteModal && (
                <DeleteModal
                    siteName={customer.customerName}
                    toggleDeleteModal={() => setDeleteModal(false)}
                    type="customer"
                />
            )}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {isNew ? "New Site" : "Site Details"}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Site ID"
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.customerId}
                                helperText={errors.customerId}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Site Name"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.customerName}
                                helperText={errors.customerName}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Address"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.address}
                                helperText={errors.address}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="City"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.city}
                                helperText={errors.city}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="GPS Latitude"
                                value={latitude}
                                onChange={(e) => setLatitude(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.latitude}
                                helperText={errors.latitude}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="GPS Longitude"
                                value={longitude}
                                onChange={(e) => setLongitude(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                error={!!errors.longitude}
                                helperText={errors.longitude}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2, gap: 1 }}>
                    {!isNew && !dataChanged && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteModal(true)}
                            size="small"
                        >
                            Delete Site
                        </Button>
                    )}
                    {dataChanged && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={isNew ? <CloseIcon /> : <UndoIcon />}
                                onClick={handleReset}
                                size="small"
                            >
                                {isNew ? "Cancel" : "Undo"}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                size="small"
                            >
                                {isNew ? "Create Site" : "Save"}
                            </Button>
                        </>
                    )}
                </CardActions>
            </Card>
        </>
    );
};

export default CustomerEditView;
